import React, { useEffect } from "react";

/**
 * SEO component — uses direct DOM head management for maximum compatibility.
 * Sets title, description, canonical, and OG/Twitter tags per page.
 *
 * Usage:
 *   <Seo
 *     title="Ateliers d'éveil · Foundation of Light"
 *     description="…"
 *     path="/ateliers"
 *     image="/brand/logo.png"
 *     jsonLd={eventSchema}
 *   />
 */
const SITE_URL = "https://foundation-of-light.com";
const DEFAULT_IMAGE = "/brand/logo.png";

function upsertMeta(attr, value, content) {
    if (!content) return null;
    let el = document.head.querySelector(`meta[${attr}="${value}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
    return el;
}

function upsertLink(rel, href) {
    if (!href) return null;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
    return el;
}

const clampDesc = (s, n = 158) => {
    const t = String(s || "").replace(/\s+/g, " ").trim();
    if (t.length <= n) return t;
    const cut = t.slice(0, n - 1);
    return cut.slice(0, cut.lastIndexOf(" ") > n * 0.6 ? cut.lastIndexOf(" ") : cut.length).replace(/[\s,;:.—-]+$/, "") + "…";
};

export default function Seo({ title, description: rawDescription, path = "", image, jsonLd, noindex = false }) {
    const description = clampDesc(rawDescription);
    useEffect(() => {
        const canonical = `${SITE_URL}${path}`;
        const img = image ? (image.startsWith("http") ? image : `${SITE_URL}${image}`) : `${SITE_URL}${DEFAULT_IMAGE}`;
        const prevTitle = document.title;
        if (title) document.title = title;
        upsertMeta("name", "description", description);
        upsertLink("canonical", canonical);
        upsertMeta("property", "og:title", title);
        upsertMeta("property", "og:description", description);
        upsertMeta("property", "og:url", canonical);
        upsertMeta("property", "og:image", img);
        upsertMeta("property", "og:image:alt", title);
        upsertMeta("name", "twitter:title", title);
        upsertMeta("name", "twitter:description", description);
        upsertMeta("name", "twitter:image", img);
        upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");

        // JSON-LD (page-specific structured data)
        let ldEl;
        if (jsonLd) {
            ldEl = document.createElement("script");
            ldEl.type = "application/ld+json";
            ldEl.dataset.seoPage = "1";
            ldEl.textContent = JSON.stringify(jsonLd);
            document.head.appendChild(ldEl);
        }
        return () => {
            document.title = prevTitle;
            if (ldEl && ldEl.parentNode) ldEl.parentNode.removeChild(ldEl);
        };
    }, [title, description, path, image, JSON.stringify(jsonLd), noindex]);
    return null;
}
